import { useCallback, useEffect, useRef, useState } from 'react'
import type { IdentityWorkspaceMode } from '@/types/components/audiences/import-session'
import type { InferenceDistributionEntry } from '@/types/components/audiences/inferred-mix-editor'
import type { IdentityFieldSummary } from '@/types/components/audiences/import-session'
import {
  FIELD_CONSTANT_OPTIONS,
  supportsWorkspaceMode,
} from '../lib/required-fields-ui'

const SOURCE_SEARCH_DEBOUNCE_MS = 250

function normalizeDistribution(
  entries: InferenceDistributionEntry[] | null | undefined,
) {
  if (!entries || entries.length === 0) {
    return []
  }

  return entries
    .map((entry) => ({
      value: entry.value,
      percentage: entry.percentage,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))
}

function areDistributionsEqual(
  left: InferenceDistributionEntry[] | null | undefined,
  right: InferenceDistributionEntry[] | null | undefined,
) {
  const normalizedLeft = normalizeDistribution(left)
  const normalizedRight = normalizeDistribution(right)

  if (normalizedLeft.length !== normalizedRight.length) {
    return false
  }

  return normalizedLeft.every((entry, index) => {
    const other = normalizedRight[index]
    return (
      other != null &&
      entry.value === other.value &&
      entry.percentage === other.percentage
    )
  })
}

interface UseWorkspaceDraftStateArgs {
  activeField: IdentityFieldSummary | null
  currentMode: IdentityWorkspaceMode | null
  fallbackMode: IdentityWorkspaceMode | null
  setWorkspaceMode: (mode: IdentityWorkspaceMode | null) => void
  workspaceMode: IdentityWorkspaceMode | null
}

export interface WorkspaceDraftState {
  draftSourceColumn: string
  draftConstantValue: string
  mappingDirty: boolean
  mappingBusy: boolean
  sourceSearch: string
  debouncedSourceSearch: string
  sourceDropdownOpen: boolean
  draftDistribution: InferenceDistributionEntry[] | null
  /**
   * True only when the user has intentionally changed a value from the
   * initialized baseline. Explicit backend-provided source selection
   * initialization (Effect 2 in RequiredFieldsStep) does NOT set isDirty,
   * eliminating the false-positive "Discard unsaved edits?" prompt that
   * appeared even without user interaction.
   */
  isDirty: boolean
  /** Reset transient draft UI state when switching fields. */
  reset: () => void
  /**
   * Set draftSourceColumn AND update the baseline used for isDirty in one call.
   * Use this for programmatic (non-user-initiated) updates — e.g. when source
   * options load and auto-select the first candidate. Calling setDraftSourceColumn
   * directly would also move the baseline, but this name makes the intent explicit.
   */
  setDraftSourceColumnAndBaseline: (v: string) => void
  setDraftSourceColumn: (v: string) => void
  setDraftConstantValue: (v: string) => void
  setMappingDirty: (v: boolean) => void
  setMappingBusy: (v: boolean) => void
  setSourceSearch: (v: string) => void
  setSourceDropdownOpen: (v: boolean) => void
  setDraftDistribution: (v: InferenceDistributionEntry[] | null) => void
  /**
   * True when the current draft source column was chosen from the column picker
   * in this session. Used to enable "Map to question" after search is cleared:
   * the refetched candidate list is only the first page and may omit the pick.
   */
  draftSourceColumnMatchesTrustedPick: boolean
}

/**
 * Owns all mutable draft state for the identity field workspace.
 *
 * ### Why isDirty uses initialSourceColumn, not currentSourceColumn
 *
 * The server's `currentSourceColumn` may be null when a field is unset.
 * When the backend later provides an explicit selected source, Effect 2 in
 * RequiredFieldsStep seeds it via `setDraftSourceColumnAndBaseline`. Both the
 * draft value and the initial baseline are set together.
 *
 * Result: isDirty stays false until the user explicitly picks a DIFFERENT value.
 * Previously, isDirty was computed as
 * `draftSourceColumn !== (currentSourceColumn ?? "")`, which always fired after
 * programmatic initialization.
 *
 * ### Call order in RequiredFieldsStep
 *
 * This hook must be called BEFORE useIdentitySourceOptions so it can supply
 * `debouncedSourceSearch` to that hook. The hook itself
 * does NOT depend on safeSourceOptions — the step handles late-population via
 * an effect that calls `draft.setDraftSourceColumnAndBaseline(...)`.
 */
export function useWorkspaceDraftState({
  activeField,
  currentMode,
  fallbackMode,
  setWorkspaceMode,
  workspaceMode,
}: UseWorkspaceDraftStateArgs): WorkspaceDraftState {
  const [draftSourceColumn, setDraftSourceColumnState] = useState('')
  const [draftConstantValue, setDraftConstantValue] = useState('')
  const [mappingDirty, setMappingDirty] = useState(false)
  const [mappingBusy, setMappingBusy] = useState(false)
  const [sourceSearch, setSourceSearch] = useState('')
  const [debouncedSourceSearch, setDebouncedSourceSearch] = useState('')
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false)
  const [draftDistribution, setDraftDistribution] = useState<
    InferenceDistributionEntry[] | null
  >(null)

  /**
   * Tracks the value draftSourceColumn was LAST set to programmatically
   * (initialization or auto-population). isDirty is true only if the user has
   * changed draftSourceColumn away from this baseline.
   */
  const initialSourceColumnRef = useRef<string>('')

  /**
   * Tracks the value draftConstantValue was initialized to for the current
   * field. isDirty for set_constant mode compares against this ref.
   */
  const initialConstantValueRef = useRef<string>('')
  const initialDistributionRef = useRef<InferenceDistributionEntry[] | null>(null)

  const initializedFieldRef = useRef<string | null>(null)
  /** Source column last chosen from the picker (not programmatic baseline). */
  const [trustedPickerSourceColumn, setTrustedPickerSourceColumn] = useState<
    string | null
  >(null)

  const reset = useCallback(() => {
    setSourceSearch('')
    setSourceDropdownOpen(false)
    setMappingDirty(false)
    setMappingBusy(false)
    setTrustedPickerSourceColumn(null)
  }, [])

  /**
   * Set draftSourceColumn and simultaneously update the isDirty baseline.
   * Call this for programmatic selection so that initialization is NOT treated
   * as a user edit.
   */
  const setDraftSourceColumnAndBaseline = useCallback((v: string) => {
    setTrustedPickerSourceColumn(null)
    setDraftSourceColumnState(v)
    initialSourceColumnRef.current = v
  }, [])

  /**
   * Set draftSourceColumn from a user interaction. The baseline is NOT updated,
   * so isDirty becomes true if the new value differs from the initial baseline.
   */
  const setDraftSourceColumn = useCallback((v: string) => {
    setTrustedPickerSourceColumn(v.trim() ? v : null)
    setDraftSourceColumnState(v)
  }, [])

  // --- Effect 1: Initialize draft state when the active field changes ---
  useEffect(() => {
    if (!activeField) {
      return
    }
    const key = activeField.canonicalId
    const nextMode =
      currentMode && supportsWorkspaceMode(activeField, currentMode)
        ? currentMode
        : fallbackMode
    if (initializedFieldRef.current === key) {
      // Same field — only fix up mode if it's become invalid (e.g. after a save).
      // map_values is a valid transitional mode set by handlePatchSuccess even
      // though supportsWorkspaceMode would return false for it.
      if (
        workspaceMode &&
        !supportsWorkspaceMode(activeField, workspaceMode) &&
        workspaceMode !== 'map_values'
      ) {
        setWorkspaceMode(fallbackMode)
      }
      return
    }
    initializedFieldRef.current = key
    setWorkspaceMode(nextMode)

    // Init source column from the server-persisted value (candidates aren't
    // available yet — Effect 2 in RequiredFieldsStep handles late-population).
    const initialSource = activeField.currentSourceColumn ?? ''
    setTrustedPickerSourceColumn(null)
    setDraftSourceColumnState(initialSource)
    initialSourceColumnRef.current = initialSource

    const initialConstant = activeField.currentConstantValue ?? ''
    setDraftConstantValue(initialConstant)
    initialConstantValueRef.current = initialConstant

    setSourceSearch('')
    setMappingDirty(false)
    setMappingBusy(false)

    const suggestedMap = new Map(
      (activeField.suggestedInference?.values ?? []).map((v) => [
        v.value,
        v.percentage,
      ]),
    )
    const initialDistribution = (
      FIELD_CONSTANT_OPTIONS[activeField.canonicalId] ?? []
    ).map((value) => ({
        value,
        percentage: suggestedMap.get(value) ?? 0,
      }))
    setDraftDistribution(initialDistribution)
    initialDistributionRef.current = initialDistribution
  }, [
    activeField,
    currentMode,
    fallbackMode,
    setWorkspaceMode,
    workspaceMode,
  ])

  // --- Effect 2: Debounce source search input ---
  useEffect(() => {
    if (currentMode !== 'confirm_source' && currentMode !== 'map_values') {
      setDebouncedSourceSearch('')
      return
    }
    if (sourceSearch.length === 0) {
      setDebouncedSourceSearch('')
      return
    }
    const id = window.setTimeout(
      () => setDebouncedSourceSearch(sourceSearch),
      SOURCE_SEARCH_DEBOUNCE_MS,
    )
    return () => window.clearTimeout(id)
  }, [currentMode, sourceSearch])

  const isDirty =
    currentMode === 'confirm_source'
      ? draftSourceColumn !== initialSourceColumnRef.current
      : currentMode === 'set_constant'
        ? draftConstantValue !== initialConstantValueRef.current
        : currentMode === 'review_inference'
          ? !areDistributionsEqual(
              draftDistribution,
              initialDistributionRef.current,
            )
        : currentMode === 'map_values'
          ? mappingDirty
          : false

  const draftSourceColumnMatchesTrustedPick =
    Boolean(draftSourceColumn.trim()) &&
    trustedPickerSourceColumn === draftSourceColumn

  return {
    draftSourceColumn,
    draftConstantValue,
    mappingDirty,
    mappingBusy,
    sourceSearch,
    debouncedSourceSearch,
    sourceDropdownOpen,
    draftDistribution,
    isDirty,
    reset,
    setDraftSourceColumnAndBaseline,
    setDraftSourceColumn,
    setDraftConstantValue,
    setMappingDirty,
    setMappingBusy,
    setSourceSearch,
    setSourceDropdownOpen,
    setDraftDistribution,
    draftSourceColumnMatchesTrustedPick,
  }
}
